import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartLegend, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { t } from '@/i18n';
import type { PayrollMonth } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { MONTH_LABELS, PlnDeltaTooltip, formatAxis, orderedLegendContent } from './chart-utils';

/**
 * "Gdzie idą pieniądze" — every złoty the employer spends on an employee,
 * split into what the employee actually receives and what goes to the state.
 * The five segments sum exactly to `employerCost` (gross = net + pit +
 * employeeZus + health; employerCost = gross + employerZus), so the stack
 * height IS the employer's total cost.
 *
 * Bottom→top order is deliberate: net first (what lands in the pocket), then
 * the wedge, growing away from the baseline. Segment colors take categorical
 * slots 1..5 in that same order — the slot order is what the CVD check
 * validates for *adjacent* pairs, and in a stack only neighbours touch.
 *
 * `months` holds ELAPSED months only in the current year. We map it straight
 * through instead of zero-filling to 12: a zero-filled tail would draw a cliff
 * to zero that reads as "payroll collapsed in July" rather than "July hasn't
 * happened yet".
 */
const STACK_ORDER = ['net', 'pit', 'employeeZus', 'health', 'employerZus'] as const;

export function PayrollCostStackChart({ months, year }: { months: PayrollMonth[]; year: number }) {
  const chartData = months.map((m) => ({
    month: MONTH_LABELS[m.month - 1],
    net: m.net,
    pit: m.pit,
    employeeZus: m.employeeZus,
    health: m.health,
    employerZus: m.employerZus,
    employerCost: m.employerCost,
  }));

  const config: ChartConfig = {
    net: { label: t('dashboard.people.net'), color: 'var(--chart-1)' },
    pit: { label: t('dashboard.people.pit'), color: 'var(--chart-2)' },
    employeeZus: { label: t('dashboard.people.employeeZus'), color: 'var(--chart-3)' },
    health: { label: t('dashboard.people.health'), color: 'var(--chart-4)' },
    employerZus: { label: t('dashboard.people.employerZus'), color: 'var(--chart-5)' },
  };

  if (chartData.length === 0) {
    return (
      <ChartCard title={t('dashboard.people.costStackTitle')}>
        <p className="text-muted-foreground py-12 text-center text-sm">
          {t('dashboard.people.noPayroll')}
        </p>
      </ChartCard>
    );
  }

  return (
    <ChartCard title={t('dashboard.people.costStackTitle')}>
      <p className="text-muted-foreground mb-3 text-xs">{t('dashboard.people.costStackHint')}</p>
      <ChartContainer config={config} className="h-[320px] w-full">
        <BarChart data={chartData}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={64} />
          <ChartTooltip
            content={
              <PlnDeltaTooltip
                config={config}
                year={year}
                deltaLabel={t('dashboard.people.employerCostTotal')}
                deltaKey="employerCost"
              />
            }
          />
          <ChartLegend content={orderedLegendContent([...STACK_ORDER])} />
          {STACK_ORDER.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="payroll"
              fill={`var(--color-${key})`}
              maxBarSize={28}
              // 2px surface gap between segments — the spacer, not a border.
              stroke="var(--card)"
              strokeWidth={2}
              // Rounded data-end on the topmost segment only; square at the baseline.
              radius={i === STACK_ORDER.length - 1 ? [4, 4, 0, 0] : undefined}
            />
          ))}
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
